import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../api/axios'
import {
  BuildingOfficeIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  UsersIcon,
  CircleStackIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  ArrowPathIcon,
  CheckIcon,
  ChevronDownIcon,
  ArrowDownTrayIcon,
  QrCodeIcon,
} from '@heroicons/react/24/outline'
import QRCode from 'qrcode'
import { WrenchScrewdriverIcon, HeartIcon, EnvelopeIcon, RocketLaunchIcon } from '@heroicons/react/24/outline'
import ReprovisionModal from '../components/ReprovisionModal'
import HealthCheckModal from '../components/HealthCheckModal'
import CompanyAccessManagerModal from '../components/CompanyAccessManagerModal'
import AddCompanyPanel, { type CreatedCompany } from '../components/companies/AddCompanyPanel'
import SetupPanel, { type SetupCompany } from '../components/companies/SetupPanel'
import { KeyIcon } from '@heroicons/react/24/outline'

interface Company {
  id: string
  company_name: string
  company_code: string
  database_name: string
  status: 'active' | 'suspended' | 'inactive' | 'trial'
  subscription_tier: 'basic' | 'standard' | 'premium' | 'enterprise'
  provisioning_status?: string
  /** Setup state from the server's activation log (CITS-Server setupSummary). */
  setup?: { tracked: boolean; state: 'ready' | 'building' | 'failed' | 'waiting' | 'untracked' | 'held' | 'declined'; failedStep: string | null; error: string | null }
  /** Photo storage (CITS-Server docs/photo-storage-api.md). Absent on older servers. */
  photo_storage_ready?: boolean
  storage_provider?: string | null
  contact_name?: string
  contact_email?: string
  contact_phone?: string
  database_host?: string
  database_port?: string
  database_user?: string
  superadmin_user?: string
  superadmin_password?: string
  app_service_user?: string
  app_service_password?: string
  stack_project_id?: string
  stack_public_key?: string
  admin_url_slug?: string
  auth_provider?: string | null
  stack_auth_configured_at?: string
  zitadel_configured_at?: string
  zitadel_org_id?: string
  zitadel_client_id?: string
  created_at: string
  trial_ends_at?: string
  trial_days_left?: number
  admin_count?: number
  reporting_config?: {
    type: 'weekly' | 'bi-weekly' | 'custom'
    weekStartDay: string
    weekEndDay: string
    biWeeklyStartDate: string | null
  }
}

interface PermanentDeleteReceipt {
  backup_file?: string
  dropped_db?: string
  zitadel_org_deactivated?: boolean
  purged?: boolean
}

const STORAGE_LABEL: Record<string, string> = { sharepoint: 'SharePoint', s3: 'S3', rustfs: 'RustFS' }

/** Storage column: the provider, or "None"; amber when a provider is chosen but not fully filled in. */
function StorageCell({ company }: { company: Company }) {
  if (company.photo_storage_ready === undefined && company.storage_provider === undefined) {
    return <span className="text-xs text-slate-400">—</span>
  }
  const provider = company.storage_provider || null
  if (!provider) return <span className="text-xs text-slate-500">None</span>
  const label = STORAGE_LABEL[provider] || provider
  if (company.photo_storage_ready === false) {
    return (
      <span className="text-xs font-medium text-amber-700" title="Chosen but not fully set up — photos are off for this company">
        {label} (incomplete)
      </span>
    )
  }
  return <span className="text-xs font-medium text-emerald-700">{label}</span>
}

interface CompanyAdmin {
  id: string
  company_id: string
  email: string
  name?: string
  role: string
  is_active: boolean
  is_company_owner: boolean
  onboarding_completed_for_company: boolean
  onboarding_completed_at?: string
  added_by: string
  created_at: string
  updated_at: string
}

interface ZitadelDiagnostic {
  found: boolean
  inThisOrg: boolean
  userId: string | null
  state: string | null
  emailVerified: boolean | null
  loginNames: string[]
  preferredLoginName: string | null
  otherOrg: { orgId: string | null } | null
  userLoginMustBeDomain: boolean
  loginBlockers: string[]
}

const ZITADEL_BLOCKER_LABELS: Record<string, string> = {
  userDoesNotExist: 'No user with this email exists anywhere',
  notInOrg: 'User exists, but only in a different org',
  emailUnverified: 'Email is not verified',
  userNotInitialized: 'User has not set a password yet (INITIAL state)',
  userLoginMustBeDomain: 'Org requires login as email@org-domain — signing in with the plain email fails',
}

interface DryRunResult {
  ok: boolean
  issues: string[]
  warnings: string[]
  summary?: {
    subscription_tier?: string
    status?: string
    admin_email?: string | null
    admin_url_slug?: string | null
    auto_enable_features?: number
  }
  recommendations?: string[]
}

export default function Companies() {
  const navigate = useNavigate()
  const [companies, setCompanies] = useState<Company[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'setupFailed' | 'trial' | 'active' | 'suspended' | 'inactive'>('all')
  const [setupCompany, setSetupCompany] = useState<SetupCompany | null>(null)
  const [highlightedId, setHighlightedId] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [editingCompany, setEditingCompany] = useState<Company | null>(null)
  
  // Admin management state
  const [showAdminModal, setShowAdminModal] = useState(false)
  const [selectedCompanyForAdmins, setSelectedCompanyForAdmins] = useState<Company | null>(null)
  const [companyAdmins, setCompanyAdmins] = useState<CompanyAdmin[]>([])
  const [loadingAdmins, setLoadingAdmins] = useState(false)
  const [adminFormData, setAdminFormData] = useState({
    email: '',
    name: '',
    role: 'admin',
    is_company_owner: false
  })
  // Zitadel account-management state (keyed by lowercased email)
  const [zitadelDiag, setZitadelDiag] = useState<Record<string, ZitadelDiagnostic>>({})
  const [zitadelBusy, setZitadelBusy] = useState<Record<string, boolean>>({})
  const [lookupEmail, setLookupEmail] = useState('')
  const [lookupDiag, setLookupDiag] = useState<ZitadelDiagnostic | null>(null)
  
  const [formData, setFormData] = useState({
    company_name: '',
    company_code: '',
    database_name: '',
    status: 'active' as 'active' | 'suspended' | 'inactive' | 'trial',
    subscription_tier: 'standard' as 'basic' | 'standard' | 'premium' | 'enterprise',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    admin_email: '', // Initial admin email for portal access
    database_host: '',
    database_port: '',
    database_user: '',
    database_password: '',
    superadmin_user: '',
    superadmin_password: '',
    app_service_user: '',
    app_service_password: '',
    stack_project_id: '',
    stack_public_key: '',
    stack_secret_key: '',
    admin_url_slug: '',
    reporting_type: 'custom' as 'weekly' | 'bi-weekly' | 'custom',
    reporting_weekStartDay: 'monday',
    reporting_weekEndDay: 'sunday',
    reporting_biWeeklyStartDate: '',
  })
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [dryRunResult, setDryRunResult] = useState<DryRunResult | null>(null)
  const [dryRunLoading, setDryRunLoading] = useState(false)
  
  // Activate trial state
  const [showActivateModal, setShowActivateModal] = useState(false)
  const [activatingCompany, setActivatingCompany] = useState<Company | null>(null)
  const [activateLoading, setActivateLoading] = useState(false)
  const [activateFormData, setActivateFormData] = useState({
    subscriptionTier: 'basic' as 'basic' | 'standard' | 'premium' | 'enterprise',
  })

  // Database provisioning state
  const [selectedSqlFile, setSelectedSqlFile] = useState<File | null>(null)
  const [provisioningStatus, setProvisioningStatus] = useState<'idle' | 'provisioning' | 'success' | 'error'>('idle')
  const [provisioningMessage, setProvisioningMessage] = useState('')
  const [showProvisioningModal, setShowProvisioningModal] = useState(false)
  const [provisioningCompanyId, setProvisioningCompanyId] = useState<string | null>(null)
  const [provisioningSource, setProvisioningSource] = useState<'template' | 'file'>('template')
  const [availableTemplates, setAvailableTemplates] = useState<Array<{ filename: string; size: number; modifiedAt: string; isDefault: boolean }>>([])
  const [selectedTemplate, setSelectedTemplate] = useState<string>('')

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isSelecting, setIsSelecting] = useState(false)
  const [bulkLoading, setBulkLoading] = useState(false)
  const [showBulkTierMenu, setShowBulkTierMenu] = useState(false)
  const [showBulkStatusMenu, setShowBulkStatusMenu] = useState(false)

  // Login As state
  const [loginAsLoading, setLoginAsLoading] = useState<string | null>(null)
  const [migratingId, setMigratingId] = useState<string | null>(null)
  const [backfillingUris, setBackfillingUris] = useState(false)

  // QR Code modal state
  const [qrCompany, setQrCompany] = useState<Company | null>(null)
  // New three-step Add company panel (Edit company keeps the full modal).
  const [showAddPanel, setShowAddPanel] = useState(false)
  const [qrDataUrl, setQrDataUrl] = useState<string>('')
  const [qrLoading, setQrLoading] = useState(false)

  // Reprovision modal state
  const [reprovisionCompany, setReprovisionCompany] = useState<Company | null>(null)

  // Health check modal state
  const [healthCheckCompany, setHealthCheckCompany] = useState<Company | null>(null)

  // Access manager modal state
  const [accessManagerCompany, setAccessManagerCompany] = useState<Company | null>(null)

  // Resend-welcome state (button spinner per company row)
  const [resendingWelcomeId, setResendingWelcomeId] = useState<string | null>(null)

  // Search
  const [searchQuery, setSearchQuery] = useState('')

  // Permanent delete modal state
  const [permanentDeleteCompany, setPermanentDeleteCompany] = useState<Company | null>(null)
  const [permanentDeleteStep, setPermanentDeleteStep] = useState<1 | 2>(1)
  const [permanentDeleteConfirmCode, setPermanentDeleteConfirmCode] = useState('')
  const [permanentDeleteLoading, setPermanentDeleteLoading] = useState(false)
  const [permanentDeleteError, setPermanentDeleteError] = useState('')
  const [permanentDeleteReceipt, setPermanentDeleteReceipt] = useState<PermanentDeleteReceipt | null>(null)

  // Trial → Active conversion
  const [convertingId, setConvertingId] = useState<string | null>(null)

  const handleConvertToActive = async (company: Company) => {
    const confirmed = window.confirm(
      `Convert "${company.company_name}" from trial to active?\n\n` +
      `• Status will change to active\n` +
      `• Trial end date will be cleared\n` +
      `• Subscription tier stays "${company.subscription_tier}"\n` +
      `• A confirmation email will be sent to ${(company as any).contact_email || 'the contact on file'}`
    )
    if (!confirmed) return
    setConvertingId(company.id)
    setError('')
    setSuccess('')
    try {
      const { data } = await api.post(
        `/super-admin/companies/${company.id}/convert-to-active`,
        { sendEmail: true }
      )
      setSuccess(
        data.alreadyActive
          ? `${company.company_name} is already active.`
          : `${company.company_name} converted to active${data.email?.ok ? ' — confirmation email sent.' : '.'}`
      )
      setTimeout(() => setSuccess(''), 4000)
      fetchCompanies()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to convert trial to active')
      setTimeout(() => setError(''), 5000)
    } finally {
      setConvertingId(null)
    }
  }

  const handleResendWelcomeEmail = async (company: Company) => {
    const defaultTo = (company as any).contact_email || ''
    const to = window.prompt(
      `Resend the welcome email for "${company.company_name}"?\n\nRecipient (leave as-is to use contact email):`,
      defaultTo
    )
    if (to === null) return
    const target = (to || '').trim()
    if (target && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(target)) {
      window.alert('Invalid email address')
      return
    }
    setResendingWelcomeId(company.id)
    try {
      const { data } = await api.post(
        `/super-admin/companies/${company.id}/resend-welcome-email`,
        target && target !== defaultTo ? { to: target } : {}
      )
      setSuccess(`Welcome email sent to ${data.sentTo}`)
      setTimeout(() => setSuccess(''), 4000)
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to resend welcome email')
      setTimeout(() => setError(''), 5000)
    } finally {
      setResendingWelcomeId(null)
    }
  }

  useEffect(() => {
    fetchCompanies()
    fetchEnvDefaults()
  }, [])

  // Deep-link support: /companies#company=<id> scrolls and highlights that card.
  useEffect(() => {
    if (loading || companies.length === 0) return
    const m = window.location.hash.match(/company=([\w-]+)/)
    if (!m) return
    const id = m[1]
    const target = companies.find(c => c.id === id)
    if (!target) return
    setHighlightedId(id)
    requestAnimationFrame(() => {
      document.getElementById(`company-card-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
    const t = setTimeout(() => setHighlightedId(null), 3000)
    return () => clearTimeout(t)
  }, [loading, companies])

  const fetchEnvDefaults = async () => {
    try {
      const response = await api.get('/super-admin/service-account-defaults')
      // Pre-populate with server defaults
      // Note: superadmin_user is for provisioning, app_service_user is for runtime
      setFormData(prev => ({
        ...prev,
        database_host: response.data.database_host || 'localhost',
        database_port: response.data.database_port || '3306',
        database_user: response.data.app_service_user || 'app-service', // Runtime: app-service
        superadmin_user: response.data.superadmin_user || 'super-admin', // Provisioning: super-admin
        app_service_user: response.data.app_service_user || 'app-service'
      }))
    } catch (err: any) {
      // Use hardcoded defaults if endpoint fails
      setFormData(prev => ({
        ...prev,
        database_host: 'localhost',
        database_port: '3306',
        database_user: 'app-service',
        superadmin_user: 'super-admin',
        app_service_user: 'app-service'
      }))
    }
  }

  const fetchCompanies = async () => {
    try {
      // Fetch all companies — no status filter, so we get pending trials too
      const response = await api.get('/super-admin/companies')
      setCompanies(response.data.companies || [])
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch companies')
    } finally {
      setLoading(false)
    }
  }

  // A company whose setup has started (Add company, or an approved signup) is
  // past "pending": its Setup tag says how far it got.
  const isActivated = (c: Company) => !!(c.zitadel_configured_at || c.stack_auth_configured_at || c.auth_provider === 'better-auth'
    || (c.setup && ['building', 'failed', 'ready'].includes(c.setup.state)))
  const setupFailedCount = companies.filter(c => c.setup?.state === 'failed').length
  const pendingCount = companies.filter(c => c.status === 'trial' && !isActivated(c)).length

  const filteredCompanies = (() => {
    let base: Company[]
    switch (statusFilter) {
      case 'pending': base = companies.filter(c => c.status === 'trial' && !isActivated(c)); break
      case 'setupFailed': base = companies.filter(c => c.setup?.state === 'failed'); break
      case 'trial':   base = companies.filter(c => c.status === 'trial' && isActivated(c)); break
      case 'active':  base = companies.filter(c => c.status === 'active'); break
      case 'suspended': base = companies.filter(c => c.status === 'suspended'); break
      case 'inactive':  base = companies.filter(c => c.status === 'inactive'); break
      default:        base = companies
    }
    if (!searchQuery.trim()) return base
    const q = searchQuery.toLowerCase()
    return base.filter(c =>
      c.company_name.toLowerCase().includes(q) ||
      c.company_code.toLowerCase().includes(q) ||
      (c.contact_email || '').toLowerCase().includes(q)
    )
  })()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    try {
      // Stack Auth tokens are automatically attached by axios interceptor via cookies
      const submitData = buildSubmitData()
      
      if (editingCompany) {
        await api.put(`/super-admin/companies/${editingCompany.id}`, submitData)
        setSuccess('Company updated successfully')
      } else {
        await api.post('/super-admin/companies', submitData)
        setSuccess('Company created successfully')
      }
      
      // Refresh the companies list
      fetchCompanies()
      
      // Close modal after a brief delay to show success message
      setTimeout(() => {
        setShowModal(false)
        resetForm()
        setDryRunResult(null)
      }, 1500)
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save company')
    }
  }

  const buildSubmitData = () => {
    const submitData: any = { ...formData }

    // Handle Stack secret key: if unchanged on edit, send marker to keep existing
    if (editingCompany && !formData.stack_secret_key) {
      submitData.stack_secret_key = '__KEEP_EXISTING__'
    }
    
    // Build reporting config
    submitData.reporting_config = {
      type: formData.reporting_type,
      weekStartDay: formData.reporting_weekStartDay,
      weekEndDay: formData.reporting_weekEndDay,
      biWeeklyStartDate: formData.reporting_type === 'bi-weekly' ? formData.reporting_biWeeklyStartDate : null
    }

    return submitData
  }

  const handleDryRun = async () => {
    setError('')
    setSuccess('')
    setDryRunResult(null)
    setDryRunLoading(true)

    try {
      // Stack Auth tokens are automatically attached by axios interceptor via cookies
      const submitData = buildSubmitData()
      const response = await api.post('/super-admin/companies/dry-run', submitData)
      setDryRunResult(response.data)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Dry-run validation failed')
    } finally {
      setDryRunLoading(false)
    }
  }

  const handleEdit = (company: Company) => {
    setEditingCompany(company)
    setDryRunResult(null)
    setFormData({
      company_name: company.company_name,
      company_code: company.company_code,
      database_name: company.database_name,
      status: company.status,
      subscription_tier: company.subscription_tier,
      contact_name: company.contact_name || '',
      contact_email: company.contact_email || '',
      contact_phone: company.contact_phone || '',
      admin_email: '', // Allow updating admin email when editing
      database_host: company.database_host || '',
      database_port: company.database_port || '',
      database_user: company.database_user || '',
      database_password: '', // Don't show encrypted password
      superadmin_user: company.superadmin_user || '',
      superadmin_password: '', // Don't show encrypted password
      app_service_user: company.app_service_user || '',
      app_service_password: '', // Don't show encrypted password
      stack_project_id: company.stack_project_id || '',
      stack_public_key: company.stack_public_key || '',
      stack_secret_key: '', // Don't show encrypted secret
      admin_url_slug: company.admin_url_slug || '',
      reporting_type: company.reporting_config?.type || 'custom',
      reporting_weekStartDay: company.reporting_config?.weekStartDay || 'monday',
      reporting_weekEndDay: company.reporting_config?.weekEndDay || 'sunday',
      reporting_biWeeklyStartDate: company.reporting_config?.biWeeklyStartDate || '',
    })
    setShowModal(true)
  }

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to deactivate ${name}? This will set it to inactive.`)) return

    try {
      // Stack Auth tokens are automatically attached by axios interceptor via cookies
      await api.delete(`/super-admin/companies/${id}`)
      setSuccess('Company deactivated successfully')
      fetchCompanies()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to deactivate company')
    }
  }

  const openPermanentDeleteModal = (company: Company) => {
    setPermanentDeleteCompany(company)
    setPermanentDeleteStep(1)
    setPermanentDeleteConfirmCode('')
    setPermanentDeleteLoading(false)
    setPermanentDeleteError('')
    setPermanentDeleteReceipt(null)
  }

  const closePermanentDeleteModal = () => {
    if (permanentDeleteLoading) return
    setPermanentDeleteCompany(null)
    setPermanentDeleteStep(1)
    setPermanentDeleteConfirmCode('')
    setPermanentDeleteError('')
    setPermanentDeleteReceipt(null)
  }

  const handlePermanentDelete = async () => {
    if (!permanentDeleteCompany) return
    if (permanentDeleteConfirmCode !== permanentDeleteCompany.company_code) {
      setPermanentDeleteError('Code does not match — type the company code exactly.')
      return
    }
    setPermanentDeleteLoading(true)
    setPermanentDeleteError('')
    try {
      const { data } = await api.delete(
        `/super-admin/companies/${permanentDeleteCompany.id}/permanent`,
        { data: { confirm_code: permanentDeleteConfirmCode } }
      )
      setPermanentDeleteReceipt(data)
      setSuccess(
        `${permanentDeleteCompany.company_name} permanently deleted. ` +
        (data.backup_file ? `Backup: ${data.backup_file}` : '')
      )
      fetchCompanies()
    } catch (err: any) {
      const code = err.response?.data?.code
      if (code === 'MUST_DEACTIVATE_FIRST') {
        setPermanentDeleteError('This company must be deactivated (inactive) before permanent deletion.')
      } else if (code === 'CONFIRM_MISMATCH') {
        setPermanentDeleteError('Confirmation code did not match on the server.')
      } else {
        setPermanentDeleteError(err.response?.data?.message || 'Permanent deletion failed.')
      }
    } finally {
      setPermanentDeleteLoading(false)
    }
  }

  // ============================================================
  // DATABASE PROVISIONING FUNCTIONS
  // ============================================================

  const handleSqlFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (!file.name.endsWith('.sql')) {
        setError('Please select a valid SQL file (.sql)')
        return
      }
      if (file.size > 50 * 1024 * 1024) { // 50MB limit
        setError('File size exceeds 50MB limit')
        return
      }
      setSelectedSqlFile(file)
      setError('')
    }
  }

  const openProvisioningModal = async (company: Company) => {
    setProvisioningCompanyId(company.id)
    setSelectedSqlFile(null)
    setProvisioningStatus('idle')
    setProvisioningMessage('')
    setProvisioningSource('template')
    setSelectedTemplate('')
    setShowProvisioningModal(true)
    try {
      const { data } = await api.get('/super-admin/schema-templates')
      const list = data.templates || []
      setAvailableTemplates(list)
      const def = list.find((t: any) => t.isDefault) || list[0]
      if (def) setSelectedTemplate(def.filename)
    } catch {
      setAvailableTemplates([])
    }
  }

  const handleProvisionDatabase = async () => {
    if (!provisioningCompanyId || !editingCompany) {
      setError('No company loaded')
      return
    }
    if (provisioningSource === 'file' && !selectedSqlFile) {
      setError('Please select a SQL file')
      return
    }
    if (provisioningSource === 'template' && !selectedTemplate) {
      setError('Please select a stored template')
      return
    }

    setProvisioningStatus('provisioning')

    try {
      const payload: Record<string, unknown> = {
        databaseName: editingCompany.database_name,
        dbHost: formData.database_host || 'localhost',
        dbPort: formData.database_port || '3306',
        dbUser: formData.database_user || 'app-service',
        dbPassword: formData.database_password,
        superadminUser: formData.superadmin_user,
        superadminPassword: formData.superadmin_password,
        appServiceUser: formData.app_service_user,
        appServicePassword: formData.app_service_password,
      }

      if (provisioningSource === 'template') {
        setProvisioningMessage(`Using stored template: ${selectedTemplate}…`)
        payload.templateName = selectedTemplate
      } else {
        setProvisioningMessage('Reading SQL file...')
        const fileContent = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = e => resolve(e.target?.result as string)
          reader.onerror = e => reject(e)
          reader.readAsText(selectedSqlFile!)
        })
        setProvisioningMessage('Encoding SQL content...')
        payload.sqlContent = btoa(unescape(encodeURIComponent(fileContent)))
      }

      setProvisioningMessage('Sending provisioning request...')
      const response = await api.post(
        `/super-admin/companies/${provisioningCompanyId}/provision`,
        payload
      )

      if (response.data.success) {
        const schemaCheck = response.data.schemaCheck as
          | { inSync: boolean; missingTables: string[]; missingColumns: Record<string, string[]> }
          | null
          | undefined
        const schemaCheckLine = schemaCheck
          ? schemaCheck.inSync
            ? '✅ Matches the schema template.'
            : `⚠️ Doesn't match the schema template — missing ${schemaCheck.missingTables.length} table(s)` +
              `${Object.keys(schemaCheck.missingColumns).length > 0 ? ` and columns on ${Object.keys(schemaCheck.missingColumns).length} table(s)` : ''}. ` +
              `Check the template's Schema Source is current on the Schema Templates page.`
          : ''

        setProvisioningStatus(schemaCheck && !schemaCheck.inSync ? 'error' : 'success')
        setProvisioningMessage(
          `✅ Database provisioned successfully!\n` +
          `Tables created: ${response.data.result?.steps?.schemaImport?.details?.tablesCreated || 0}\n` +
          `Database: ${response.data.databaseName}` +
          (schemaCheckLine ? `\n${schemaCheckLine}` : '')
        )
        setSuccess('Database provisioned successfully!')

        // Auto-close modal after 3 seconds — but only when the schema check
        // didn't flag a problem; a drifted new tenant deserves a look, not
        // an auto-dismissed toast.
        if (!schemaCheck || schemaCheck.inSync) {
          setTimeout(() => {
            setShowProvisioningModal(false)
            fetchCompanies()
          }, 3000)
        }
      } else {
        setProvisioningStatus('error')
        setProvisioningMessage(
          `⚠️ Provisioning completed with warnings:\n` +
          `${response.data.result?.steps?.schemaImport?.details?.errorsEncountered?.join('\n') || 'See logs for details'}`
        )
      }
    } catch (err: any) {
      setProvisioningStatus('error')
      const errorMsg = err.response?.data?.message || err.message || 'Unknown error'
      setProvisioningMessage(`❌ Provisioning failed:\n${errorMsg}`)
      setError(errorMsg)
    }
  }

  // ============================================================
  // TRIAL ACTIVATION FUNCTIONS
  // ============================================================

  const openActivateModal = (company: Company) => {
    setActivatingCompany(company)
    setActivateFormData({
      subscriptionTier: (company.subscription_tier as any) || 'basic',
    })
    setError('')
    setSuccess('')
    setShowActivateModal(true)
  }

  const handleActivateTrial = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!activatingCompany) return
    setActivateLoading(true)
    setError('')
    setSuccess('')

    try {
      const response = await api.post(
        `/super-admin/companies/${activatingCompany.id}/activate-trial`,
        {
          subscriptionTier: activateFormData.subscriptionTier,
        }
      )
      setSuccess(response.data.message || 'Trial activated successfully')
      setShowActivateModal(false)
      setActivatingCompany(null)
      fetchCompanies()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to activate trial')
    } finally {
      setActivateLoading(false)
    }
  }

  // ============================================================
  // ADMIN MANAGEMENT FUNCTIONS
  // ============================================================

  const fetchCompanyAdmins = async (company: Company) => {
    setLoadingAdmins(true)
    setError('')
    try {
      // Stack Auth tokens are automatically attached by axios interceptor via cookies
      const response = await api.get(`/super-admin/companies/${company.id}/admins`)
      const admins: CompanyAdmin[] = response.data.admins || []
      setCompanyAdmins(admins)
      setSelectedCompanyForAdmins(company)
      setZitadelDiag({})
      setLookupDiag(null)
      setLookupEmail('')
      setShowAdminModal(true)
      loadZitadelStatuses(company, admins)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch company admins')
    } finally {
      setLoadingAdmins(false)
    }
  }

  // ─── Zitadel account management ─────────────────────────────────────────────

  const setBusy = (key: string, value: boolean) =>
    setZitadelBusy(prev => ({ ...prev, [key]: value }))

  const storeDiag = (email: string, diag: ZitadelDiagnostic) =>
    setZitadelDiag(prev => ({ ...prev, [email.toLowerCase()]: diag }))

  const loadZitadelStatuses = async (company: Company, admins: CompanyAdmin[]) => {
    if (!company.zitadel_org_id) return
    await Promise.all(admins.map(async (a) => {
      try {
        const { data } = await api.get(
          `/super-admin/companies/${company.id}/zitadel/user`,
          { params: { email: a.email } }
        )
        storeDiag(a.email, data.diagnostic)
      } catch { /* leave status unknown */ }
    }))
  }

  const handleVerifyEmail = async (email: string) => {
    if (!selectedCompanyForAdmins) return
    setError(''); setBusy(`verify:${email.toLowerCase()}`, true)
    try {
      const { data } = await api.post(
        `/super-admin/companies/${selectedCompanyForAdmins.id}/zitadel/verify-email`,
        { email }
      )
      storeDiag(email, data.diagnostic)
      setSuccess(`Email verified for ${email}`)
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to verify ${email}`)
    } finally {
      setBusy(`verify:${email.toLowerCase()}`, false)
    }
  }

  const handleResendSetup = async (email: string) => {
    if (!selectedCompanyForAdmins) return
    setError(''); setBusy(`resend:${email.toLowerCase()}`, true)
    try {
      await api.post(
        `/super-admin/companies/${selectedCompanyForAdmins.id}/zitadel/resend-setup`,
        { email }
      )
      setSuccess(`Setup link sent to ${email}`)
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to send setup link to ${email}`)
    } finally {
      setBusy(`resend:${email.toLowerCase()}`, false)
    }
  }

  const handleEnsureUser = async (email: string, name?: string) => {
    if (!selectedCompanyForAdmins) return
    setError(''); setBusy(`create:${email.toLowerCase()}`, true)
    try {
      const { data } = await api.post(
        `/super-admin/companies/${selectedCompanyForAdmins.id}/zitadel/ensure-user`,
        { email, name: name || null }
      )
      storeDiag(email, data.diagnostic)
      setSuccess(`Account ready for ${email} (${data.result?.source || 'ok'})`)
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to create account for ${email}`)
    } finally {
      setBusy(`create:${email.toLowerCase()}`, false)
    }
  }

  const handleDisableRegister = async () => {
    if (!selectedCompanyForAdmins) return
    setError(''); setBusy('disable-register', true)
    try {
      await api.post(`/super-admin/companies/${selectedCompanyForAdmins.id}/zitadel/disable-register`)
      setSuccess('Self-registration disabled for this org')
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to disable self-registration')
    } finally {
      setBusy('disable-register', false)
    }
  }

  const handlePasswordLogin = async () => {
    if (!selectedCompanyForAdmins) return
    if (!confirm('Switch this tenant to password login (disables passkey/device login for its users)?')) return
    setError(''); setBusy('password-login', true)
    try {
      await api.post(`/super-admin/companies/${selectedCompanyForAdmins.id}/zitadel/password-login`)
      setSuccess('This tenant now uses password login (passkey disabled)')
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to switch to password login')
    } finally {
      setBusy('password-login', false)
    }
  }

  const handleDisableInstanceRegister = async () => {
    if (!confirm('Turn off the "Register" option on the Zitadel login screen for ALL tenants? This changes the instance-wide default login policy.')) return
    setError(''); setBusy('disable-instance-register', true)
    try {
      const { data } = await api.post('/super-admin/zitadel/instance/disable-register')
      setSuccess(data.unchanged
        ? 'Registration was already disabled platform-wide'
        : 'Registration disabled on the login screen (platform-wide)')
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to disable registration platform-wide')
    } finally {
      setBusy('disable-instance-register', false)
    }
  }

  const handleLookup = async () => {
    if (!selectedCompanyForAdmins) return
    const email = lookupEmail.trim()
    if (!email) return
    setError(''); setLookupDiag(null); setBusy('lookup', true)
    try {
      const { data } = await api.get(
        `/super-admin/companies/${selectedCompanyForAdmins.id}/zitadel/user`,
        { params: { email } }
      )
      setLookupDiag(data.diagnostic)
      storeDiag(email, data.diagnostic)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Lookup failed')
    } finally {
      setBusy('lookup', false)
    }
  }

  // Per-email Zitadel status chip + inline actions. Returns null when the
  // company has no Zitadel org (nothing to act on).
  const renderZitadelActions = (email: string) => {
    if (!selectedCompanyForAdmins?.zitadel_org_id) return null
    const key = email.toLowerCase()
    const diag = zitadelDiag[key]
    const busy = (action: string) => !!zitadelBusy[`${action}:${key}`]

    const [chipClass, chipText] = !diag
      ? ['bg-slate-100 text-slate-500', 'Checking…']
      : !diag.found
      ? ['bg-slate-100 text-slate-600', 'No Zitadel user']
      : !diag.inThisOrg
      ? ['bg-amber-100 text-amber-700', 'In another org']
      : diag.emailVerified
      ? ['bg-emerald-100 text-emerald-700', 'Verified ✓']
      : ['bg-amber-100 text-amber-700', 'Unverified ⚠']

    const canVerify = diag?.inThisOrg && diag?.emailVerified === false
    const canResend = diag?.inThisOrg === true
    const canCreate = !!diag && (!diag.found || !diag.inThisOrg)
    const blockers = diag?.loginBlockers ?? []

    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${chipClass}`}>Zitadel: {chipText}</span>
        {canVerify && (
          <button disabled={busy('verify')} onClick={() => handleVerifyEmail(email)}
            className="inline-flex items-center px-2 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-wait transition-colors">
            {busy('verify') ? 'Verifying…' : '✓ Verify email'}
          </button>
        )}
        {canResend && (
          <button disabled={busy('resend')} onClick={() => handleResendSetup(email)}
            className="inline-flex items-center px-2 py-0.5 rounded border border-blue-300 bg-blue-50 text-[11px] font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-wait transition-colors">
            {busy('resend') ? 'Sending…' : 'Resend setup'}
          </button>
        )}
        {canCreate && (
          <button disabled={busy('create')} onClick={() => handleEnsureUser(email)}
            className="inline-flex items-center px-2 py-0.5 rounded border border-blue-300 bg-blue-50 text-[11px] font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-wait transition-colors">
            {busy('create') ? 'Creating…' : '+ Create account'}
          </button>
        )}
        {diag?.inThisOrg && diag?.emailVerified === true && (
          <span className="text-[10px] text-slate-400">verified — nothing to do</span>
        )}
        {blockers.length > 0 && (
          <span className="text-[10px] text-amber-600" title={blockers.map(b => ZITADEL_BLOCKER_LABELS[b] || b).join(' • ')}>
            ⚠ {blockers.length} login blocker{blockers.length > 1 ? 's' : ''}
          </span>
        )}
      </div>
    )
  }

  const handleAddAdmin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedCompanyForAdmins || !adminFormData.email.trim()) {
      setError('Email is required')
      return
    }

    setError('')
    try {
      // Stack Auth tokens are automatically attached by axios interceptor via cookies
      const response = await api.post(
        `/super-admin/companies/${selectedCompanyForAdmins.id}/admins`,
        {
          email: adminFormData.email.trim(),
          name: adminFormData.name || null,
          role: adminFormData.role,
          is_company_owner: adminFormData.is_company_owner ? 1 : 0
        }
      )
      
      setCompanyAdmins([...companyAdmins, response.data])
      setAdminFormData({ email: '', name: '', role: 'super-admin', is_company_owner: false })
      setSuccess('Admin added successfully')
      
      // Refresh companies list to see any changes
      fetchCompanies()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add admin')
    }
  }

  const handleDeleteAdmin = async (adminId: string, adminEmail: string) => {
    if (!confirm(`Are you sure you want to remove ${adminEmail} from this company?`)) return
    
    if (!selectedCompanyForAdmins) return

    setError('')
    try {
      // Stack Auth tokens are automatically attached by axios interceptor via cookies
      await api.delete(
        `/super-admin/companies/${selectedCompanyForAdmins.id}/admins/${adminId}`
      )
      
      setCompanyAdmins(companyAdmins.filter(a => a.id !== adminId))
      setSuccess('Admin removed successfully')
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove admin')
    }
  }

  const handleEditAdminOwner = async (admin: CompanyAdmin, isOwner: boolean) => {
    if (!selectedCompanyForAdmins) return

    setError('')
    try {
      // Stack Auth tokens are automatically attached by axios interceptor via cookies
      await api.put(
        `/super-admin/companies/${selectedCompanyForAdmins.id}/admins/${admin.id}`,
        { is_company_owner: isOwner ? 1 : 0 }
      )
      
      // Update local state
      setCompanyAdmins(companyAdmins.map(a => 
        a.id === admin.id ? { ...a, is_company_owner: isOwner } : a
      ))
      setSuccess('Admin owner status updated')
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update admin')
    }
  }

  const handleNormalizeEmployeeId = async (admin: CompanyAdmin) => {
    if (!selectedCompanyForAdmins) return

    setError('')
    try {
      const response = await api.post(
        `/super-admin/companies/${selectedCompanyForAdmins.id}/normalize-employee-id`,
        { email: admin.email }
      )
      setSuccess(response.data?.message || (response.data?.normalized
        ? `Employee ID normalized to ${response.data.employee_id}`
        : 'Employee ID already normalized'))
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to normalize employee ID')
    }
  }

  const handleUpdateAdminRole = async (admin: CompanyAdmin, newRole: string) => {
    if (!selectedCompanyForAdmins || newRole === admin.role) return

    setError('')
    try {
      // Stack Auth tokens are automatically attached by axios interceptor via cookies
      await api.put(
        `/super-admin/companies/${selectedCompanyForAdmins.id}/admins/${admin.id}`,
        { role: newRole }
      )

      // Update local state
      setCompanyAdmins(companyAdmins.map(a =>
        a.id === admin.id ? { ...a, role: newRole } : a
      ))
      setSuccess(`Role updated to ${newRole} for ${admin.email}`)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update admin role')
    }
  }

  const closeAdminModal = () => {
    setShowAdminModal(false)
    setSelectedCompanyForAdmins(null)
    setCompanyAdmins([])
    setAdminFormData({ email: '', name: '', role: 'super-admin', is_company_owner: false })
    setZitadelDiag({})
    setLookupDiag(null)
    setLookupEmail('')
    // editingAdmin is read-only, no setter needed
  }

  const resetForm = () => {
    setEditingCompany(null)
    setFormData({
      company_name: '',
      company_code: '',
      database_name: '',
      status: 'active',
      subscription_tier: 'standard',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      admin_email: '',
      database_host: '',
      database_port: '',
      database_user: '',
      database_password: '',
      superadmin_user: '',
      superadmin_password: '',
      app_service_user: '',
      app_service_password: '',
      stack_project_id: '',
      stack_public_key: '',
      stack_secret_key: '',
      admin_url_slug: '',
      reporting_type: 'custom',
      reporting_weekStartDay: 'sunday',
      reporting_weekEndDay: 'saturday',
      reporting_biWeeklyStartDate: '',
    })
    setDryRunResult(null)
  }

  // Toggle a single company selection
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Select / deselect all visible
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredCompanies.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredCompanies.map(c => c.id)))
    }
  }

  const exitSelectMode = () => {
    setIsSelecting(false)
    setSelectedIds(new Set())
    setShowBulkTierMenu(false)
    setShowBulkStatusMenu(false)
  }

  const handleBulkChangeTier = async (tier: string) => {
    if (!selectedIds.size) return
    setBulkLoading(true)
    setShowBulkTierMenu(false)
    try {
      await Promise.all(
        [...selectedIds].map(id =>
          api.put(`/super-admin/companies/${id}`, { subscription_tier: tier })
        )
      )
      setSuccess(`Updated ${selectedIds.size} compan${selectedIds.size === 1 ? 'y' : 'ies'} to ${tier} tier.`)
      fetchCompanies()
      exitSelectMode()
    } catch {
      setError('One or more updates failed — check each company.')
    } finally {
      setBulkLoading(false)
    }
  }

  const handleBulkChangeStatus = async (status: string) => {
    if (!selectedIds.size) return
    setBulkLoading(true)
    setShowBulkStatusMenu(false)
    try {
      await Promise.all(
        [...selectedIds].map(id =>
          api.put(`/super-admin/companies/${id}`, { status })
        )
      )
      setSuccess(`Updated ${selectedIds.size} compan${selectedIds.size === 1 ? 'y' : 'ies'} to ${status}.`)
      fetchCompanies()
      exitSelectMode()
    } catch {
      setError('One or more status changes failed.')
    } finally {
      setBulkLoading(false)
    }
  }

  const handleBulkExport = () => {
    const selected = filteredCompanies.filter(c => selectedIds.has(c.id))
    const rows = [
      ['Company Name', 'Company Code', 'Status', 'Tier', 'Contact Email', 'Created'],
      ...selected.map(c => [c.company_name, c.company_code, c.status, c.subscription_tier, c.contact_email || '', new Date(c.created_at).toLocaleDateString()]),
    ]
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `companies-export-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
    exitSelectMode()
  }

  const handleLoginAs = async (company: Company) => {
    const isActivatedForLogin = !!(company.zitadel_configured_at || company.zitadel_org_id || company.stack_auth_configured_at || company.auth_provider === 'better-auth')
    if (!isActivatedForLogin) {
      setError(`${company.company_name} hasn't been activated yet. Activate it first before logging in as this company.`)
      return
    }
    setLoginAsLoading(company.id)
    try {
      const res = await api.post(`/super-admin/companies/${company.id}/generate-access-link`)
      const url = res.data?.access_url || res.data?.url
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer')
      } else {
        setError('No access URL returned from server. Ensure the generate-access-link endpoint is implemented.')
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('generate-access-link endpoint not yet implemented on the backend.')
      } else {
        setError(err.response?.data?.message || 'Failed to generate access link.')
      }
    } finally {
      setLoginAsLoading(null)
    }
  }

  const handleBackfillZitadelUris = async () => {
    const msg =
      `Backfill Zitadel redirect URIs for all provisioned tenants?\n\n` +
      `• Adds the current prod URLs (.cruzitapps.com) and legacy URLs (.cmapps.dev) to every Zitadel client\n` +
      `• Existing URIs are kept; nothing is removed\n` +
      `• Safe to run multiple times\n\n` +
      `Proceed?`
    if (!confirm(msg)) return
    setBackfillingUris(true)
    setError('')
    setSuccess('')
    try {
      const { data } = await api.post('/super-admin/zitadel/backfill-redirect-uris')
      const { total, updated_count, unchanged_count, failed_count, failed } = data
      let msg = `Backfill complete — ${total} tenants checked: ${updated_count} updated, ${unchanged_count} already current`
      if (failed_count > 0) {
        msg += `, ${failed_count} failed`
        const firstErr = failed?.[0]
        if (firstErr) msg += ` (e.g. ${firstErr.company}: ${firstErr.error})`
      }
      setSuccess(msg)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to backfill Zitadel redirect URIs.')
    } finally {
      setBackfillingUris(false)
    }
  }

  const handleMigrateToZitadel = async (company: Company) => {
    const msg =
      `Migrate "${company.company_name}" to Zitadel?\n\n` +
      `• Creates a Zitadel organization, OIDC app, and user for every active admin\n` +
      `• Each admin gets a password-setup email\n` +
      `• Stack Auth data is left intact — you can roll back by clearing the zitadel_* columns\n\n` +
      `Proceed?`
    if (!confirm(msg)) return
    setMigratingId(company.id)
    setError('')
    setSuccess('')
    try {
      const { data } = await api.post(`/super-admin/companies/${company.id}/migrate-to-zitadel`)
      setSuccess(data.message || 'Migration complete.')
      fetchCompanies()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to migrate tenant to Zitadel.')
    } finally {
      setMigratingId(null)
    }
  }

  const openQrModal = async (company: Company) => {
    setQrCompany(company)
    setQrDataUrl('')
    setQrLoading(true)
    try {
      const url = await QRCode.toDataURL(company.company_code, {
        width: 400,
        margin: 2,
        color: { dark: '#1e3c72', light: '#ffffff' },
      })
      setQrDataUrl(url)
    } catch {
      setError('Failed to generate QR code')
    } finally {
      setQrLoading(false)
    }
  }

  const downloadQr = () => {
    if (!qrDataUrl || !qrCompany) return
    const a = document.createElement('a')
    a.href = qrDataUrl
    a.download = `${qrCompany.company_code}-qr.png`
    a.click()
  }

  const printQr = () => {
    if (!qrDataUrl || !qrCompany) return
    const win = window.open('', '_blank')
    if (!win) return
    win.document.write(`
      <html><head><title>QR Code - ${qrCompany.company_name}</title></head>
      <body style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;font-family:sans-serif;gap:16px">
        <h2 style="color:#1e3c72;margin:0">${qrCompany.company_name}</h2>
        <p style="color:#6b7280;margin:0">Company Code: ${qrCompany.company_code}</p>
        <img src="${qrDataUrl}" style="width:300px;height:300px" />
        <script>window.onload=()=>{window.print();window.close()}</script>
      </body></html>
    `)
    win.document.close()
  }

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      active: 'bg-emerald-100 text-emerald-700',
      trial: 'bg-amber-100 text-amber-700',
      suspended: 'bg-red-100 text-red-700',
      inactive: 'bg-slate-100 text-slate-600',
    }
    return map[status] || 'bg-slate-100 text-slate-600'
  }

  const tierBadge = (tier: string) => {
    const map: Record<string, string> = {
      enterprise: 'bg-violet-100 text-violet-700',
      premium: 'bg-blue-100 text-blue-700',
      standard: 'bg-slate-100 text-slate-700',
      basic: 'bg-slate-50 text-slate-500',
    }
    return map[tier] || 'bg-slate-100 text-slate-600'
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 pt-5 pb-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-sm">
                <BuildingOfficeIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Companies</h1>
                <p className="text-sm text-slate-500 mt-0.5">Manage tenant companies and their configurations</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {isSelecting ? (
                <>
                  <span className="text-sm text-slate-500">{selectedIds.size} selected</span>
                  <button
                    onClick={toggleSelectAll}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    {selectedIds.size === filteredCompanies.length ? 'Deselect All' : 'Select All'}
                  </button>
                  <button onClick={exitSelectMode} className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
                    <XMarkIcon className="w-4 h-4" /> Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setIsSelecting(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <CheckIcon className="w-4 h-4" />
                    Select
                  </button>
                  <button onClick={() => navigate('/storage-config')} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">
                    <Cog6ToothIcon className="w-4 h-4" />
                    Storage
                  </button>
                  <button
                    onClick={handleBackfillZitadelUris}
                    disabled={backfillingUris}
                    title="Re-register callback URLs on every provisioned Zitadel client"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <WrenchScrewdriverIcon className={`w-4 h-4 ${backfillingUris ? 'animate-pulse' : ''}`} />
                    {backfillingUris ? 'Backfilling…' : 'Fix Zitadel URIs'}
                  </button>
                  <button onClick={() => setShowAddPanel(true)} className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 shadow-sm transition-colors">
                    <PlusIcon className="w-4 h-4" />
                    Add Company
                  </button>
                </>
              )}
            </div>
          </div>
          {/* Status filter tabs */}
          <div className="flex items-center gap-1 overflow-x-auto pb-px">
            {([
              { key: 'all' as const, label: 'All', count: companies.length, highlight: false },
              { key: 'pending' as const, label: 'Pending Activation', count: pendingCount, highlight: pendingCount > 0 },
              { key: 'setupFailed' as const, label: 'Setup failed', count: setupFailedCount, highlight: setupFailedCount > 0 },
              { key: 'active' as const, label: 'Active', count: companies.filter(c => c.status === 'active').length, highlight: false },
              { key: 'trial' as const, label: 'Trial (Active)', count: companies.filter(c => c.status === 'trial' && isActivated(c)).length, highlight: false },
              { key: 'suspended' as const, label: 'Suspended', count: companies.filter(c => c.status === 'suspended').length, highlight: false },
              { key: 'inactive' as const, label: 'Inactive', count: companies.filter(c => c.status === 'inactive').length, highlight: false },
            ]).map(tab => (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key as any)}
                className={[
                  'flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors',
                  statusFilter === tab.key
                    ? tab.highlight ? 'border-amber-500 text-amber-700' : 'border-blue-600 text-blue-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300',
                ].join(' ')}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className={[
                    'inline-flex items-center justify-center rounded-full text-xs font-semibold min-w-[18px] h-[18px] px-1',
                    statusFilter === tab.key && tab.highlight ? 'bg-amber-100 text-amber-700' :
                    statusFilter === tab.key ? 'bg-blue-100 text-blue-700' :
                    tab.highlight ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-600',
                  ].join(' ')}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Alerts */}
        {error && (
          <div className="mb-6 flex items-center gap-3 p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
            <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
            {error}
            <button onClick={() => setError('')} className="ml-auto"><XMarkIcon className="w-4 h-4" /></button>
          </div>
        )}
        {success && (
          <div className="mb-6 flex items-center gap-3 p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-700">
            <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
            {success}
            <button onClick={() => setSuccess('')} className="ml-auto"><XMarkIcon className="w-4 h-4" /></button>
          </div>
        )}

        {/* Pending Trial Requests Banner — shown on All tab when pending exist */}
        {!loading && statusFilter === 'all' && pendingCount > 0 && (
          <div className="mb-6 flex items-center gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors" onClick={() => setStatusFilter('pending')}>
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800">
                {pendingCount} pending trial{pendingCount > 1 ? 's' : ''} awaiting activation
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                Click to view — these companies need Stack Auth credentials before login access is sent.
              </p>
            </div>
            <span className="text-xs font-medium text-amber-600 whitespace-nowrap">View pending →</span>
          </div>
        )}

        {/* Search bar */}
        <div className="mb-4">
          <input
            type="search"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by name, code, or contact email…"
            className="w-full max-w-sm px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow bg-white"
          />
        </div>

        {loading ? (
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100 animate-pulse">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="px-4 py-3 flex items-center gap-4">
                <div className="h-4 bg-slate-200 rounded w-40" />
                <div className="h-4 bg-slate-200 rounded w-16 ml-auto" />
              </div>
            ))}
          </div>
        ) : filteredCompanies.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <BuildingOfficeIcon className="w-12 h-12 text-slate-300 mb-3" />
            <p className="text-slate-500 font-medium">No companies found</p>
            <p className="text-slate-400 text-sm mt-1">
              {statusFilter === 'pending' ? 'No pending trial activations — all trials have been configured.' : 'Try a different filter or search.'}
            </p>
          </div>
        ) : (
          <>
          {/* Dense table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  {isSelecting && (
                    <th className="w-10 pl-4 py-2.5">
                      <button
                        onClick={toggleSelectAll}
                        className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                          selectedIds.size === filteredCompanies.length && filteredCompanies.length > 0
                            ? 'bg-blue-600 border-blue-600'
                            : 'bg-white border-slate-300 hover:border-blue-400'
                        }`}
                      >
                        {selectedIds.size === filteredCompanies.length && filteredCompanies.length > 0 && (
                          <CheckIcon className="w-2.5 h-2.5 text-white" />
                        )}
                      </button>
                    </th>
                  )}
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Company</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Tier</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Storage</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Admins</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Created</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredCompanies.map(company => {
                  const isPendingActivation = !isActivated(company)
                  const isSelected = selectedIds.has(company.id)
                  const isInactive = company.status === 'inactive'
                  return (
                    <tr
                      key={company.id}
                      id={`company-card-${company.id}`}
                      className={`group transition-colors ${
                        highlightedId === company.id
                          ? 'bg-violet-50 ring-2 ring-inset ring-violet-300'
                          : isSelected
                          ? 'bg-blue-50'
                          : isInactive
                          ? 'opacity-60 hover:opacity-80 hover:bg-slate-50'
                          : isPendingActivation
                          ? 'bg-amber-50/40 hover:bg-amber-50'
                          : 'hover:bg-slate-50'
                      }`}
                    >
                      {/* Select checkbox */}
                      {isSelecting && (
                        <td className="pl-4 py-2.5 w-10">
                          <button
                            onClick={() => toggleSelect(company.id)}
                            className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                              isSelected ? 'bg-blue-600 border-blue-600' : 'bg-white border-slate-300 hover:border-blue-400'
                            }`}
                          >
                            {isSelected && <CheckIcon className="w-2.5 h-2.5 text-white" />}
                          </button>
                        </td>
                      )}

                      {/* Company name + code */}
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-slate-900 leading-snug">{company.company_name}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="font-mono text-xs text-slate-500">{company.company_code}</span>
                          {isPendingActivation && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                              Pending
                            </span>
                          )}
                          {company.setup?.state === 'failed' && (
                            <button
                              type="button"
                              onClick={() => setSetupCompany(company)}
                              title={company.setup.error || 'A setup step failed'}
                              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold text-red-700 bg-red-100 hover:bg-red-200"
                            >
                              Setup failed
                            </button>
                          )}
                          {company.photo_storage_ready === false && (
                            <button
                              type="button"
                              onClick={() => navigate('/storage-config')}
                              title="The crew can't take or send photos until storage is set up. Open Storage Config."
                              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold text-red-700 bg-red-100 hover:bg-red-200"
                            >
                              No photo storage
                            </button>
                          )}
                          {company.setup?.state === 'building' && (
                            <button
                              type="button"
                              onClick={() => setSetupCompany(company)}
                              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold text-blue-700 bg-blue-100 hover:bg-blue-200"
                            >
                              Setting up
                            </button>
                          )}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-3 py-2.5">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${statusBadge(company.status)}`}>
                          {company.status}
                        </span>
                      </td>

                      {/* Tier */}
                      <td className="px-3 py-2.5">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${tierBadge(company.subscription_tier)}`}>
                          {company.subscription_tier}
                        </span>
                      </td>

                      {/* Photo storage */}
                      <td className="px-3 py-2.5 hidden md:table-cell">
                        <StorageCell company={company} />
                      </td>

                      {/* Admins count */}
                      <td className="px-3 py-2.5 hidden md:table-cell">
                        <button
                          onClick={() => fetchCompanyAdmins(company)}
                          className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-violet-700 transition-colors"
                          title="Manage admins"
                        >
                          <UsersIcon className="w-3.5 h-3.5" />
                          {company.admin_count ?? '—'}
                        </button>
                      </td>

                      {/* Created */}
                      <td className="px-3 py-2.5 hidden lg:table-cell">
                        <span className="text-xs text-slate-500">
                          {new Date(company.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-2.5 text-right">
                        {isSelecting ? (
                          <button
                            onClick={() => toggleSelect(company.id)}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${isSelected ? 'text-blue-700 bg-blue-100' : 'text-slate-500 hover:bg-slate-100'}`}
                          >
                            <CheckIcon className="w-3 h-3" />
                            {isSelected ? 'Selected' : 'Select'}
                          </button>
                        ) : (
                          <div className="flex items-center justify-end gap-1 flex-wrap">
                            {/* Activate (pending only) */}
                            {isPendingActivation && (
                              <button onClick={() => openActivateModal(company)} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-md transition-colors">
                                <CheckCircleIcon className="w-3.5 h-3.5" /> Activate
                              </button>
                            )}
                            {/* Login As */}
                            {!isPendingActivation && (
                              <button
                                onClick={() => handleLoginAs(company)}
                                disabled={loginAsLoading === company.id}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors disabled:opacity-50"
                                title="Login as this company"
                              >
                                {loginAsLoading === company.id
                                  ? <ArrowPathIcon className="w-3 h-3 animate-spin" />
                                  : <ArrowTopRightOnSquareIcon className="w-3 h-3" />}
                                Login as
                              </button>
                            )}
                            {/* Setup: run any setup step again */}
                            <button
                              onClick={() => setSetupCompany(company)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
                              title="Setup steps"
                            >
                              <ArrowPathIcon className="w-3 h-3" />
                              Setup
                            </button>
                            {/* Storage */}
                            <button
                              onClick={() => navigate('/storage-config')}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors"
                              title="Storage configuration"
                            >
                              <CircleStackIcon className="w-3 h-3" />
                              Storage
                            </button>
                            {/* Edit */}
                            <button onClick={() => handleEdit(company)} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors">
                              <PencilSquareIcon className="w-3 h-3" /> Edit
                            </button>
                            {/* Admins (visible on mobile too) */}
                            <button onClick={() => fetchCompanyAdmins(company)} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-600 hover:text-violet-700 hover:bg-violet-50 rounded-md transition-colors md:hidden">
                              <UsersIcon className="w-3 h-3" /> Admins
                            </button>
                            {/* More actions — migrate, reprovision, health, access, resend, convert, QR */}
                            {!!company.stack_auth_configured_at && !company.zitadel_org_id && (
                              <button
                                onClick={() => handleMigrateToZitadel(company)}
                                disabled={migratingId === company.id}
                                title="Provision Zitadel org"
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-md transition-colors disabled:opacity-50"
                              >
                                {migratingId === company.id ? <ArrowPathIcon className="w-3 h-3 animate-spin" /> : <ArrowPathIcon className="w-3 h-3" />}
                                {migratingId === company.id ? 'Migrating…' : 'Zitadel'}
                              </button>
                            )}
                            <button onClick={() => { handleEdit(company); openProvisioningModal(company) }} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-md transition-colors">
                              <CircleStackIcon className="w-3 h-3" /> Provision
                            </button>
                            <button onClick={() => setReprovisionCompany(company)} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-600 hover:text-amber-700 hover:bg-amber-50 rounded-md transition-colors">
                              <WrenchScrewdriverIcon className="w-3 h-3" /> Reprovision
                            </button>
                            <button onClick={() => setHealthCheckCompany(company)} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-600 hover:text-rose-700 hover:bg-rose-50 rounded-md transition-colors">
                              <HeartIcon className="w-3 h-3" /> Health
                            </button>
                            <button onClick={() => setAccessManagerCompany(company)} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-md transition-colors" title="Access manager">
                              <KeyIcon className="w-3 h-3" /> Access
                            </button>
                            <button
                              onClick={() => handleResendWelcomeEmail(company)}
                              disabled={resendingWelcomeId === company.id}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50"
                            >
                              {resendingWelcomeId === company.id ? <ArrowPathIcon className="w-3 h-3 animate-spin" /> : <EnvelopeIcon className="w-3 h-3" />}
                              Welcome
                            </button>
                            {company.status === 'trial' && isActivated(company) && (
                              <button
                                onClick={() => handleConvertToActive(company)}
                                disabled={convertingId === company.id}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 rounded-md transition-colors disabled:opacity-50"
                                title="Convert trial to active"
                              >
                                {convertingId === company.id ? <ArrowPathIcon className="w-3 h-3 animate-spin" /> : <RocketLaunchIcon className="w-3 h-3" />}
                                Convert
                              </button>
                            )}
                            <button onClick={() => openQrModal(company)} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-md transition-colors">
                              <QrCodeIcon className="w-3 h-3" /> QR
                            </button>
                            {/* Lifecycle actions — separator */}
                            <span className="w-px h-4 bg-slate-200 mx-0.5" />
                            {/* Deactivate — active/trial/suspended companies */}
                            {company.status !== 'inactive' && (
                              <button
                                onClick={() => handleDelete(company.id, company.company_name)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                                title="Deactivate (set inactive)"
                              >
                                <TrashIcon className="w-3 h-3" /> Deactivate
                              </button>
                            )}
                            {/* Permanent delete — inactive companies only */}
                            {isInactive && (
                              <button
                                onClick={() => openPermanentDeleteModal(company)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-md transition-colors"
                                title="Permanently delete this company and its database"
                              >
                                <TrashIcon className="w-3 h-3" /> Delete permanently
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Bulk action floating bar */}
          {isSelecting && selectedIds.size > 0 && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 bg-slate-900 text-white rounded-2xl shadow-2xl border border-slate-700">
              <span className="text-sm font-semibold text-slate-200">
                {selectedIds.size} compan{selectedIds.size === 1 ? 'y' : 'ies'} selected
              </span>
              <div className="w-px h-5 bg-slate-600" />

              {/* Change Tier */}
              <div className="relative">
                <button
                  onClick={() => { setShowBulkTierMenu(v => !v); setShowBulkStatusMenu(false) }}
                  disabled={bulkLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors disabled:opacity-50"
                >
                  Change Tier <ChevronDownIcon className="w-3 h-3" />
                </button>
                {showBulkTierMenu && (
                  <div className="absolute bottom-full mb-2 left-0 bg-white rounded-lg border border-slate-200 shadow-xl py-1 min-w-[140px] z-10">
                    {['basic', 'standard', 'premium', 'enterprise'].map(tier => (
                      <button
                        key={tier}
                        onClick={() => handleBulkChangeTier(tier)}
                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 capitalize transition-colors"
                      >
                        {tier}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Change Status */}
              <div className="relative">
                <button
                  onClick={() => { setShowBulkStatusMenu(v => !v); setShowBulkTierMenu(false) }}
                  disabled={bulkLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors disabled:opacity-50"
                >
                  Change Status <ChevronDownIcon className="w-3 h-3" />
                </button>
                {showBulkStatusMenu && (
                  <div className="absolute bottom-full mb-2 left-0 bg-white rounded-lg border border-slate-200 shadow-xl py-1 min-w-[140px] z-10">
                    {['active', 'trial', 'suspended', 'inactive'].map(st => (
                      <button
                        key={st}
                        onClick={() => handleBulkChangeStatus(st)}
                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 capitalize transition-colors"
                      >
                        {st}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Export CSV */}
              <button
                onClick={handleBulkExport}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-600 hover:bg-emerald-500 rounded-lg transition-colors"
              >
                <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                Export CSV
              </button>

              <div className="w-px h-5 bg-slate-600" />
              <button onClick={exitSelectMode} className="text-slate-400 hover:text-white transition-colors">
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          )}
          </>
        )}

        <AddCompanyPanel
          open={showAddPanel}
          onClose={() => { setShowAddPanel(false); fetchCompanies() }}
          onCreated={() => fetchCompanies()}
          onLoginAs={(c: CreatedCompany) => handleLoginAs({ ...c, auth_provider: 'better-auth' } as Company)}
          onShowQr={(c: CreatedCompany) => openQrModal(c as Company)}
          onOpenSetup={(c: CreatedCompany) => { setShowAddPanel(false); fetchCompanies(); setSetupCompany(c) }}
        />

        <SetupPanel
          company={setupCompany}
          onClose={() => { setSetupCompany(null); fetchCompanies() }}
          onChanged={() => fetchCompanies()}
        />

        {showModal && (
          <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm overflow-y-auto p-4" onClick={() => setShowModal(false)}>
            <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">{editingCompany ? 'Edit Company' : 'Add New Company'}</h2>
                <button onClick={() => setShowModal(false)} className="p-1 rounded-md hover:bg-slate-100 transition-colors">
                  <XMarkIcon className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Company Name *</label>
                    <input type="text" value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} required
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Company Code * (for QR)</label>
                    <input type="text" value={formData.company_code} onChange={(e) => setFormData({ ...formData, company_code: e.target.value })} required
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Database Name *</label>
                    <input type="text" value={formData.database_name} onChange={(e) => setFormData({ ...formData, database_name: e.target.value })} required
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                    <p className="text-xs text-slate-400 mt-1">Name of the database for this tenant (e.g., tenant_acme)</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Status</label>
                    <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow bg-white">
                      <option value="active">Active</option>
                      <option value="trial">Trial</option>
                      <option value="suspended">Suspended</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Subscription Tier</label>
                    <select value={formData.subscription_tier} onChange={(e) => setFormData({ ...formData, subscription_tier: e.target.value as any })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow bg-white">
                      <option value="basic">Basic</option>
                      <option value="standard">Standard</option>
                      <option value="premium">Premium</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Contact Name</label>
                    <input type="text" value={formData.contact_name} onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Contact Email</label>
                    <input type="email" value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Contact Phone</label>
                    <input type="tel" value={formData.contact_phone} onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Admin Email{!editingCompany ? ' *' : ''}</label>
                    <input type="email" value={formData.admin_email} onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })} placeholder="Initial admin user email (M365)" required={!editingCompany}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                    <p className="text-xs text-slate-400 mt-1">This user will have admin portal access</p>
                  </div>

                  {/* Database Configuration */}
                  <div className="md:col-span-2 mt-4 pt-4 border-t border-slate-200">
                    <h3 className="text-sm font-semibold text-slate-900 mb-1">Database Configuration</h3>
                    <p className="text-xs text-slate-500 mb-4">Leave blank to use server defaults.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Database Host</label>
                    <input type="text" value={formData.database_host} onChange={(e) => setFormData({ ...formData, database_host: e.target.value })} placeholder="localhost"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Database Port</label>
                    <input type="number" value={formData.database_port} onChange={(e) => setFormData({ ...formData, database_port: e.target.value })} placeholder="3306"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Database User</label>
                    <input type="text" value={formData.database_user} onChange={(e) => setFormData({ ...formData, database_user: e.target.value })} placeholder="app-service"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Database Password</label>
                    <input type="password" value={formData.database_password} onChange={(e) => setFormData({ ...formData, database_password: e.target.value })} placeholder={editingCompany ? 'Keep existing' : 'Server default'}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Super Admin User</label>
                    <input type="text" value={formData.superadmin_user} onChange={(e) => setFormData({ ...formData, superadmin_user: e.target.value })} placeholder="super-admin"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Super Admin Password</label>
                    <input type="password" value={formData.superadmin_password} onChange={(e) => setFormData({ ...formData, superadmin_password: e.target.value })} placeholder={editingCompany ? 'Keep existing' : 'Server default'}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">App Service User</label>
                    <input type="text" value={formData.app_service_user} onChange={(e) => setFormData({ ...formData, app_service_user: e.target.value })} placeholder="app-service"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">App Service Password</label>
                    <input type="password" value={formData.app_service_password} onChange={(e) => setFormData({ ...formData, app_service_password: e.target.value })} placeholder={editingCompany ? 'Keep existing' : 'Server default'}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                  </div>

                  {/* Stack Auth */}
                  <div className="md:col-span-2 mt-4 pt-4 border-t border-slate-200">
                    <h3 className="text-sm font-semibold text-slate-900 mb-4">Stack Auth Configuration</h3>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Stack Project ID</label>
                    <input type="text" value={formData.stack_project_id} onChange={(e) => setFormData({ ...formData, stack_project_id: e.target.value })} placeholder="prj_xxx"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Stack Public Key</label>
                    <input type="text" value={formData.stack_public_key} onChange={(e) => setFormData({ ...formData, stack_public_key: e.target.value })} placeholder="pk_xxx"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Stack Secret Key</label>
                    <input type="password" value={formData.stack_secret_key} onChange={(e) => setFormData({ ...formData, stack_secret_key: e.target.value })} placeholder={editingCompany ? 'Keep existing' : 'ssk_xxx'}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Admin Portal Slug</label>
                    <input type="text" value={formData.admin_url_slug} onChange={(e) => setFormData({ ...formData, admin_url_slug: e.target.value })} placeholder="acme"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                    <p className="text-xs text-slate-400 mt-1">Subdomain for admin portal (e.g., acme)</p>
                  </div>

                  {/* Storage Note */}
                  <div className="md:col-span-2 mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <h3 className="text-sm font-semibold text-emerald-800 mb-1">Storage Configuration</h3>
                    <p className="text-xs text-emerald-700">SharePoint, S3, and other storage settings are configured in <strong>Settings → Storage Configuration</strong>.</p>
                  </div>

                  {/* Reporting Configuration */}
                  <div className="md:col-span-2 mt-4 pt-4 border-t border-slate-200">
                    <h3 className="text-sm font-semibold text-slate-900 mb-4">Payroll Reporting Configuration</h3>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Reporting Type *</label>
                    <select value={formData.reporting_type} onChange={(e) => setFormData({ ...formData, reporting_type: e.target.value as any })} required
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow bg-white">
                      <option value="weekly">Weekly</option>
                      <option value="bi-weekly">Bi-Weekly</option>
                      <option value="custom">Custom (Date Range)</option>
                    </select>
                  </div>
                  {formData.reporting_type !== 'custom' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Week Start Day *</label>
                        <select value={formData.reporting_weekStartDay} onChange={(e) => setFormData({ ...formData, reporting_weekStartDay: e.target.value })} required
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow bg-white">
                          {['sunday','monday','tuesday','wednesday','thursday','friday','saturday'].map(d => <option key={d} value={d} className="capitalize">{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Week End Day *</label>
                        <select value={formData.reporting_weekEndDay} onChange={(e) => setFormData({ ...formData, reporting_weekEndDay: e.target.value })} required
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow bg-white">
                          {['sunday','monday','tuesday','wednesday','thursday','friday','saturday'].map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
                        </select>
                      </div>
                    </>
                  )}
                  {formData.reporting_type === 'bi-weekly' && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Bi-Weekly Start Date *</label>
                      <input type="date" value={formData.reporting_biWeeklyStartDate} onChange={(e) => setFormData({ ...formData, reporting_biWeeklyStartDate: e.target.value })} required
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow" />
                    </div>
                  )}
                </div>

                {/* Dry Run */}
                <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Dry-run validation</p>
                      <p className="text-xs text-slate-500">Check for conflicts before saving</p>
                    </div>
                    {dryRunResult && (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${dryRunResult.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {dryRunResult.ok ? 'Ready' : 'Needs attention'}
                      </span>
                    )}
                  </div>
                  {dryRunResult ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          ['Tier', dryRunResult.summary?.subscription_tier || 'standard'],
                          ['Status', dryRunResult.summary?.status || 'active'],
                          ['Auto-features', String(dryRunResult.summary?.auto_enable_features ?? 0)],
                          ['Admin', dryRunResult.summary?.admin_email || 'Not provided'],
                        ].map(([label, val]) => (
                          <div key={label} className="bg-white p-3 rounded-md border border-slate-200">
                            <p className="text-[10px] text-slate-400 uppercase tracking-wider">{label}</p>
                            <p className="text-sm font-semibold text-slate-900 mt-0.5 truncate">{val}</p>
                          </div>
                        ))}
                      </div>
                      {dryRunResult.issues?.length > 0 && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                          <p className="text-xs font-semibold text-red-700 mb-1">Blocking issues</p>
                          <ul className="text-xs text-red-600 list-disc list-inside space-y-0.5">{dryRunResult.issues.map((i, idx) => <li key={idx}>{i}</li>)}</ul>
                        </div>
                      )}
                      {dryRunResult.warnings?.length > 0 && (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                          <p className="text-xs font-semibold text-amber-700 mb-1">Warnings</p>
                          <ul className="text-xs text-amber-600 list-disc list-inside space-y-0.5">{dryRunResult.warnings.map((i, idx) => <li key={idx}>{i}</li>)}</ul>
                        </div>
                      )}
                      {dryRunResult.recommendations && dryRunResult.recommendations.length > 0 && (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                          <p className="text-xs font-semibold text-blue-700 mb-1">Recommendations</p>
                          <ul className="text-xs text-blue-600 list-disc list-inside space-y-0.5">{dryRunResult.recommendations.map((i, idx) => <li key={idx}>{i}</li>)}</ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400">Run a dry-run to validate uniqueness, tier fit, and auto-enabled features.</p>
                  )}
                </div>

                <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200">
                  <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">Cancel</button>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={handleDryRun} disabled={dryRunLoading} className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
                      {dryRunLoading ? 'Validating…' : 'Run Dry-Run'}
                    </button>
                    <button type="submit" className="px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 shadow-sm transition-colors">
                      {editingCompany ? 'Update' : 'Create'} Company
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Admin Management Modal */}
        {showAdminModal && selectedCompanyForAdmins && (
          <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm overflow-y-auto p-4" onClick={closeAdminModal}>
            <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">Admins — {selectedCompanyForAdmins.company_name}</h2>
                <button onClick={closeAdminModal} className="p-1 rounded-md hover:bg-slate-100 transition-colors">
                  <XMarkIcon className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              {/* Modal sits above (z-50) the page-level error/success banners, so
                  actions taken in here (Add/Remove/Role/Normalize ID, etc.) need
                  their own feedback rendered inside the modal to be visible. */}
              {(error || success) && (
                <div className="px-6 pt-4">
                  {error && (
                    <div className="mb-3 flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                      <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
                      {error}
                      <button onClick={() => setError('')} className="ml-auto"><XMarkIcon className="w-4 h-4" /></button>
                    </div>
                  )}
                  {success && (
                    <div className="mb-3 flex items-center gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-700">
                      <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
                      {success}
                      <button onClick={() => setSuccess('')} className="ml-auto"><XMarkIcon className="w-4 h-4" /></button>
                    </div>
                  )}
                </div>
              )}

              <div className="p-6 max-h-[70vh] overflow-y-auto space-y-6">
                {loadingAdmins ? (
                  <div className="text-center py-8 text-sm text-slate-400">Loading admins…</div>
                ) : (
                  <>
                    {/* Add Admin Form */}
                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                      <h3 className="text-sm font-semibold text-slate-900 mb-3">Add New Admin</h3>
                      <form onSubmit={handleAddAdmin} className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Email *</label>
                            <input type="email" value={adminFormData.email} onChange={(e) => setAdminFormData({ ...adminFormData, email: e.target.value })} placeholder="admin@example.com" required
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Name</label>
                            <input type="text" value={adminFormData.name} onChange={(e) => setAdminFormData({ ...adminFormData, name: e.target.value })} placeholder="Full Name"
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Role</label>
                            <select value={adminFormData.role} onChange={(e) => setAdminFormData({ ...adminFormData, role: e.target.value })}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
                              <option value="owner">Owner</option>
                              <option value="admin">Admin</option>
                              <option value="manager">Manager</option>
                              <option value="barge-manager">Barge Manager</option>
                              <option value="worker">Worker</option>
                              <option value="super-admin">Super Admin (platform staff only)</option>
                            </select>
                          </div>
                          <div className="flex items-end">
                            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                              <input type="checkbox" checked={adminFormData.is_company_owner} onChange={(e) => setAdminFormData({ ...adminFormData, is_company_owner: e.target.checked })}
                                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                              Company Owner
                            </label>
                          </div>
                        </div>
                        <button type="submit" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
                          <PlusIcon className="w-4 h-4" /> Add Admin
                        </button>
                      </form>
                    </div>

                    {/* Current Admins */}
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900 mb-3">Current Admins ({companyAdmins.length})</h3>
                      {companyAdmins.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-6">No admins assigned yet.</p>
                      ) : (
                        <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
                          {companyAdmins.map(admin => (
                            <div key={admin.id} className="flex items-start justify-between px-4 py-3 bg-white hover:bg-slate-50 transition-colors">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
                                  <span className="truncate">{admin.email}</span>
                                  {admin.is_company_owner && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700">Owner</span>}
                                  {admin.onboarding_completed_for_company && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">Onboarded</span>}
                                </div>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <select
                                    value={admin.role}
                                    onChange={(e) => handleUpdateAdminRole(admin, e.target.value)}
                                    className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600 border-none focus:ring-1 focus:ring-blue-500 outline-none"
                                  >
                                    <option value="owner">owner</option>
                                    <option value="admin">admin</option>
                                    <option value="manager">manager</option>
                                    <option value="barge-manager">barge-manager</option>
                                    <option value="worker">worker</option>
                                    <option value="super-admin">super-admin</option>
                                  </select>
                                  <span className={`text-[10px] ${admin.is_active ? 'text-emerald-600' : 'text-slate-400'}`}>{admin.is_active ? 'Active' : 'Inactive'}</span>
                                </div>
                                {admin.is_company_owner && admin.role !== 'owner' && (
                                  <div className="mt-1 flex items-center gap-2 text-[10px] font-semibold text-red-600">
                                    <span>⚠ Marked as owner but role is "{admin.role}" — should be "owner"</span>
                                    <button
                                      type="button"
                                      onClick={() => handleUpdateAdminRole(admin, 'owner')}
                                      className="px-1.5 py-0.5 rounded bg-red-600 text-white hover:bg-red-700 transition-colors"
                                    >
                                      Fix Role
                                    </button>
                                  </div>
                                )}
                                {renderZitadelActions(admin.email)}
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <button
                                  onClick={() => handleNormalizeEmployeeId(admin)}
                                  title="Replace a stuck ADMIN_<timestamp> placeholder employee ID with a clean number (for admins mid-onboarding)"
                                  className="text-xs font-medium text-slate-500 hover:text-slate-800 transition-colors"
                                >
                                  Normalize ID
                                </button>
                                <button
                                  onClick={() => handleEditAdminOwner(admin, !admin.is_company_owner)}
                                  className="text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
                                >
                                  {admin.is_company_owner ? 'Remove Owner' : 'Make Owner'}
                                </button>
                                <button onClick={() => handleDeleteAdmin(admin.id, admin.email)} className="text-xs font-medium text-red-500 hover:text-red-700 transition-colors">
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Zitadel Org Tools */}
                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                      <h3 className="text-sm font-semibold text-slate-900 mb-1">Zitadel Account Tools</h3>
                      {!selectedCompanyForAdmins.zitadel_org_id ? (
                        <p className="text-xs text-slate-500">This company is not provisioned on Zitadel — migrate or provision the tenant first.</p>
                      ) : (
                        <>
                          <p className="text-xs text-slate-500 mb-3">Diagnose or fix any tenant login by email, or disable self-registration for this org.</p>

                          <div className="flex flex-wrap items-end gap-2 mb-3">
                            <div className="flex-1 min-w-[200px]">
                              <label className="block text-xs font-medium text-slate-600 mb-1">Look up email</label>
                              <input type="email" value={lookupEmail}
                                onChange={(e) => setLookupEmail(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleLookup() } }}
                                placeholder="user@example.com"
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
                            </div>
                            <button type="button" disabled={zitadelBusy['lookup'] || !lookupEmail.trim()} onClick={handleLookup}
                              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                              {zitadelBusy['lookup'] ? 'Looking…' : 'Look up'}
                            </button>
                            <button type="button" disabled={zitadelBusy['disable-register']} onClick={handleDisableRegister}
                              className="px-4 py-2 bg-white border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-100 disabled:opacity-40 disabled:cursor-wait transition-colors">
                              {zitadelBusy['disable-register'] ? 'Disabling…' : 'Disable self-registration'}
                            </button>
                            <button type="button" disabled={zitadelBusy['password-login']} onClick={handlePasswordLogin}
                              className="px-4 py-2 bg-white border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-100 disabled:opacity-40 disabled:cursor-wait transition-colors">
                              {zitadelBusy['password-login'] ? 'Switching…' : 'Use password login (disable passkey)'}
                            </button>
                          </div>
                          <p className="text-[10px] text-slate-400 -mt-1 mb-2">“Use password login” disables passkey/device login for <span className="font-medium">this tenant only</span> — its users get a password prompt.</p>

                          {lookupDiag && (
                            <div className="p-3 bg-white border border-slate-200 rounded-lg text-xs space-y-1">
                              <div className="flex flex-wrap gap-x-4 gap-y-1 text-slate-700">
                                <span><span className="text-slate-400">Exists:</span> {lookupDiag.found ? 'yes' : 'no'}</span>
                                <span><span className="text-slate-400">In this org:</span> {lookupDiag.inThisOrg ? 'yes' : 'no'}</span>
                                <span><span className="text-slate-400">Email verified:</span> {lookupDiag.emailVerified === null ? '—' : lookupDiag.emailVerified ? 'yes' : 'no'}</span>
                                <span><span className="text-slate-400">State:</span> {lookupDiag.state || '—'}</span>
                              </div>
                              {lookupDiag.preferredLoginName && (
                                <div className="text-slate-700"><span className="text-slate-400">Login name:</span> {lookupDiag.preferredLoginName}</div>
                              )}
                              {lookupDiag.otherOrg?.orgId && (
                                <div className="text-amber-700"><span className="text-slate-400">Found in other org:</span> {lookupDiag.otherOrg.orgId}</div>
                              )}
                              {lookupDiag.loginBlockers.length > 0 && (
                                <ul className="list-disc list-inside text-amber-700">
                                  {lookupDiag.loginBlockers.map(b => (
                                    <li key={b}>{ZITADEL_BLOCKER_LABELS[b] || b}</li>
                                  ))}
                                </ul>
                              )}
                              {renderZitadelActions(lookupEmail)}
                            </div>
                          )}
                        </>
                      )}

                      {/* Platform-wide: instance-default login policy */}
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <p className="text-[11px] font-semibold text-slate-500 mb-1">Platform-wide</p>
                        <button type="button" disabled={zitadelBusy['disable-instance-register']} onClick={handleDisableInstanceRegister}
                          className="px-3 py-1.5 bg-white border border-amber-300 text-amber-700 text-xs font-medium rounded-lg hover:bg-amber-50 disabled:opacity-40 disabled:cursor-wait transition-colors">
                          {zitadelBusy['disable-instance-register'] ? 'Disabling…' : 'Disable “Register” on login screen (all tenants)'}
                        </button>
                        <p className="text-[10px] text-slate-400 mt-1">Turns off self-registration on Zitadel’s instance-default login policy — the “Register” link on the generic login screen that per-company settings don’t control.</p>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="px-6 py-4 border-t border-slate-200 flex justify-end">
                <button onClick={closeAdminModal} className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Reprovision Modal */}
        {reprovisionCompany && (
          <ReprovisionModal
            company={reprovisionCompany}
            onClose={() => setReprovisionCompany(null)}
            onCompleted={fetchCompanies}
          />
        )}

        {/* Health Check Modal */}
        {healthCheckCompany && (
          <HealthCheckModal
            company={healthCheckCompany}
            onClose={() => setHealthCheckCompany(null)}
          />
        )}

        {/* Company Access Manager Modal */}
        {accessManagerCompany && (
          <CompanyAccessManagerModal
            company={accessManagerCompany}
            onClose={() => setAccessManagerCompany(null)}
            onApplied={fetchCompanies}
          />
        )}

        {/* Provisioning Modal */}
        {showProvisioningModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowProvisioningModal(false)}>
            <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">Provision Database</h2>
                <button onClick={() => setShowProvisioningModal(false)} className="p-1 rounded-md hover:bg-slate-100 transition-colors">
                  <XMarkIcon className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <div className="p-6">
                {provisioningStatus === 'idle' && (
                  <>
                    <div className="mb-4">
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Schema source</label>
                      <div className="flex rounded-lg border border-slate-300 overflow-hidden text-sm">
                        <button
                          type="button"
                          onClick={() => setProvisioningSource('template')}
                          className={`flex-1 px-3 py-1.5 ${provisioningSource === 'template' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                        >
                          Stored template
                        </button>
                        <button
                          type="button"
                          onClick={() => setProvisioningSource('file')}
                          className={`flex-1 px-3 py-1.5 border-l border-slate-300 ${provisioningSource === 'file' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                        >
                          Upload SQL file
                        </button>
                      </div>
                    </div>

                    {provisioningSource === 'template' ? (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Template</label>
                        {availableTemplates.length === 0 ? (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                            No stored templates found. Go to <span className="font-mono">Schema Templates</span> in the sidebar to pull one first, or switch to "Upload SQL file".
                          </div>
                        ) : (
                          <select
                            value={selectedTemplate}
                            onChange={e => setSelectedTemplate(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            {availableTemplates.map(t => (
                              <option key={t.filename} value={t.filename}>
                                {t.filename} {t.isDefault ? '(default)' : ''} — {(t.size / 1024).toFixed(1)} KB — {new Date(t.modifiedAt).toLocaleDateString()}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    ) : (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">SQL Schema File</label>
                        <input type="file" accept=".sql" onChange={handleSqlFileSelect}
                          className="w-full text-sm text-slate-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                        {selectedSqlFile && (
                          <p className="text-xs text-emerald-600 mt-2">Selected: {selectedSqlFile.name} ({(selectedSqlFile.size / 1024).toFixed(1)} KB)</p>
                        )}
                      </div>
                    )}

                    {editingCompany && (
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-sm mb-4">
                        <p className="text-slate-700"><strong>Company:</strong> {editingCompany.company_name}</p>
                        <p className="text-slate-700"><strong>Database:</strong> {editingCompany.database_name}</p>
                      </div>
                    )}
                  </>
                )}
                {provisioningStatus === 'provisioning' && (
                  <div className="text-center py-8">
                    <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-sm text-slate-600 font-mono whitespace-pre-wrap">{provisioningMessage}</p>
                  </div>
                )}
                {provisioningStatus === 'success' && (
                  <div className="text-center py-8">
                    <CheckCircleIcon className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
                    <p className="text-sm text-slate-700 font-mono whitespace-pre-wrap mb-3">{provisioningMessage}</p>
                    <p className="text-xs text-slate-400">Closing automatically…</p>
                  </div>
                )}
                {provisioningStatus === 'error' && (
                  <div className="text-center py-8">
                    <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
                    <p className="text-sm text-red-600 font-mono whitespace-pre-wrap">{provisioningMessage}</p>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
                {provisioningStatus === 'idle' ? (
                  <>
                    <button onClick={() => setShowProvisioningModal(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">Cancel</button>
                    <button
                      onClick={handleProvisionDatabase}
                      disabled={provisioningSource === 'file' ? !selectedSqlFile : !selectedTemplate}
                      className="px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      Provision Database
                    </button>
                  </>
                ) : (
                  <button onClick={() => setShowProvisioningModal(false)} disabled={provisioningStatus === 'provisioning'}
                    className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
                    {provisioningStatus === 'provisioning' ? 'Processing…' : 'Close'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Activate Trial Modal */}
        {showActivateModal && activatingCompany && (
          <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto" onClick={() => { if (!activateLoading) setShowActivateModal(false) }}>
            <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
              {/* Modal header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-amber-50 rounded-t-xl">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Activate Trial</h2>
                  <p className="text-sm text-slate-500 mt-0.5">Review the request, set the tier, then provide Stack Auth credentials to send login access.</p>
                </div>
                <button onClick={() => setShowActivateModal(false)} disabled={activateLoading} className="p-1 rounded-md hover:bg-amber-100 transition-colors disabled:opacity-50">
                  <XMarkIcon className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <form onSubmit={handleActivateTrial} className="divide-y divide-slate-100">

                {/* Step 1: Verify company info */}
                <div className="px-6 py-5">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-slate-800 text-white text-xs flex items-center justify-center font-bold">1</span>
                    Verify company info
                  </h3>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                    <div>
                      <span className="text-slate-400 text-xs uppercase tracking-wide">Company Name</span>
                      <p className="font-semibold text-slate-900 mt-0.5">{activatingCompany.company_name}</p>
                    </div>
                    <div>
                      <span className="text-slate-400 text-xs uppercase tracking-wide">Company Code</span>
                      <p className="font-mono text-slate-900 mt-0.5">{activatingCompany.company_code}</p>
                    </div>
                    <div>
                      <span className="text-slate-400 text-xs uppercase tracking-wide">Contact Name</span>
                      <p className="text-slate-800 mt-0.5">{activatingCompany.contact_name || '—'}</p>
                    </div>
                    <div>
                      <span className="text-slate-400 text-xs uppercase tracking-wide">Contact Email</span>
                      <p className="text-slate-800 mt-0.5 break-all">{activatingCompany.contact_email || '—'}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-slate-400 text-xs uppercase tracking-wide">Portal URL</span>
                      <p className="font-mono text-blue-700 mt-0.5 text-xs">
                        https://{activatingCompany.admin_url_slug || activatingCompany.company_code}.{typeof window !== 'undefined' ? (import.meta as any).env?.VITE_TRIAL_PORTAL_DOMAIN || 'cmapps.dev' : 'cmapps.dev'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Step 2: Set subscription tier */}
                <div className="px-6 py-5">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-slate-800 text-white text-xs flex items-center justify-center font-bold">2</span>
                    Set subscription tier
                  </h3>
                  <div className="grid grid-cols-4 gap-2">
                    {(['basic', 'standard', 'premium', 'enterprise'] as const).map(tier => (
                      <button
                        key={tier}
                        type="button"
                        disabled={activateLoading}
                        onClick={() => setActivateFormData({ ...activateFormData, subscriptionTier: tier })}
                        className={[
                          'py-2.5 px-3 rounded-lg border text-sm font-medium capitalize transition-all',
                          activateFormData.subscriptionTier === tier
                            ? 'border-blue-600 bg-blue-50 text-blue-700 ring-1 ring-blue-600'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50',
                        ].join(' ')}
                      >
                        {tier}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 mt-2">Features will be auto-enabled for the selected tier.</p>
                </div>

                {/* Step 3: Review actions */}
                <div className="px-6 py-5">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-slate-800 text-white text-xs flex items-center justify-center font-bold">3</span>
                    Review &amp; confirm
                  </h3>
                  <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-500 space-y-1">
                    <p className="font-medium text-slate-600">On confirmation this will:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>Enable <strong>{activateFormData.subscriptionTier}</strong>-tier features</li>
                      <li>Provision the tenant database</li>
                      <li>Create a Zitadel organization, project, and OIDC app for this company</li>
                      <li>Create the first admin user <strong>{activatingCompany.contact_email}</strong> and email a password-setup link</li>
                      <li>Send a welcome email to the customer</li>
                    </ul>
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <div className="px-6 py-3">
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                      <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
                      {error}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="px-6 py-4 flex gap-3 bg-slate-50 rounded-b-xl">
                  <button
                    type="button"
                    onClick={() => setShowActivateModal(false)}
                    disabled={activateLoading}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={activateLoading}
                    className="flex-[2] px-4 py-2.5 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {activateLoading ? (
                      <>
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                        Activating — sending emails…
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="w-4 h-4" />
                        Activate &amp; Send Login Access
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Permanent Delete Modal */}
        {permanentDeleteCompany && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={closePermanentDeleteModal}
          >
            <div
              className="bg-white rounded-xl shadow-xl border border-red-200 w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-red-100 bg-red-50 rounded-t-xl">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                    <TrashIcon className="w-4 h-4 text-red-700" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-red-900">Delete permanently</h2>
                    <p className="text-xs text-red-600 mt-0.5">{permanentDeleteCompany.company_name}</p>
                  </div>
                </div>
                <button onClick={closePermanentDeleteModal} disabled={permanentDeleteLoading} className="p-1 rounded-md hover:bg-red-100 transition-colors disabled:opacity-50">
                  <XMarkIcon className="w-5 h-5 text-red-400" />
                </button>
              </div>

              {permanentDeleteReceipt ? (
                /* Success receipt */
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <CheckCircleIcon className="w-8 h-8 text-emerald-500 flex-shrink-0" />
                    <p className="text-sm font-semibold text-slate-900">Company permanently deleted</p>
                  </div>
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-2 text-xs font-mono text-slate-700">
                    {permanentDeleteReceipt.backup_file && (
                      <p><span className="text-slate-400">Backup:</span> {permanentDeleteReceipt.backup_file}</p>
                    )}
                    {permanentDeleteReceipt.dropped_db && (
                      <p><span className="text-slate-400">DB dropped:</span> {permanentDeleteReceipt.dropped_db}</p>
                    )}
                    <p>
                      <span className="text-slate-400">Zitadel deactivated:</span>{' '}
                      {permanentDeleteReceipt.zitadel_org_deactivated ? 'yes' : 'no'}
                    </p>
                    <p>
                      <span className="text-slate-400">Registry purged:</span>{' '}
                      {permanentDeleteReceipt.purged ? 'yes' : 'no'}
                    </p>
                  </div>
                  <div className="flex justify-end">
                    <button onClick={closePermanentDeleteModal} className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">
                      Close
                    </button>
                  </div>
                </div>
              ) : permanentDeleteStep === 1 ? (
                /* Step 1 — warning */
                <div className="p-6 space-y-4">
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-2">
                    <p className="text-sm font-semibold text-red-800">This action is irreversible. The following will be destroyed:</p>
                    <ul className="text-sm text-red-700 list-disc list-inside space-y-1 mt-2">
                      <li>Tenant database: <span className="font-mono font-semibold">{permanentDeleteCompany.database_name}</span></li>
                      <li>
                        {permanentDeleteCompany.admin_count != null
                          ? `${permanentDeleteCompany.admin_count} admin account${permanentDeleteCompany.admin_count !== 1 ? 's' : ''}`
                          : 'All admin accounts for this company'}
                      </li>
                      <li>All company data, shifts, employees, and settings</li>
                      <li>Zitadel organization (if provisioned)</li>
                    </ul>
                    <p className="text-xs text-red-600 mt-2 font-medium">A backup will be saved on the server before deletion.</p>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button onClick={closePermanentDeleteModal} className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">
                      Cancel
                    </button>
                    <button
                      onClick={() => setPermanentDeleteStep(2)}
                      className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              ) : (
                /* Step 2 — confirm code */
                <div className="p-6 space-y-4">
                  <p className="text-sm text-slate-700">
                    Type the company code <span className="font-mono font-bold text-slate-900">{permanentDeleteCompany.company_code}</span> to confirm deletion:
                  </p>
                  <input
                    type="text"
                    autoFocus
                    value={permanentDeleteConfirmCode}
                    onChange={e => { setPermanentDeleteConfirmCode(e.target.value); setPermanentDeleteError('') }}
                    placeholder={permanentDeleteCompany.company_code}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-shadow"
                  />
                  {permanentDeleteError && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                      <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
                      {permanentDeleteError}
                    </div>
                  )}
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => setPermanentDeleteStep(1)}
                      disabled={permanentDeleteLoading}
                      className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={handlePermanentDelete}
                      disabled={permanentDeleteLoading || permanentDeleteConfirmCode !== permanentDeleteCompany.company_code}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {permanentDeleteLoading ? (
                        <><ArrowPathIcon className="w-4 h-4 animate-spin" /> Deleting…</>
                      ) : (
                        <><TrashIcon className="w-4 h-4" /> Delete permanently</>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* QR Code Modal */}
        {qrCompany && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setQrCompany(null)}>
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <div>
                  <h2 className="text-base font-bold text-slate-900">QR Code</h2>
                  <p className="text-xs text-slate-500 mt-0.5">{qrCompany.company_name}</p>
                </div>
                <button onClick={() => setQrCompany(null)} className="p-1 rounded-md hover:bg-slate-100 transition-colors">
                  <XMarkIcon className="w-5 h-5 text-slate-400" />
                </button>
              </div>
              <div className="px-6 py-6 flex flex-col items-center gap-4">
                {qrLoading ? (
                  <div className="w-48 h-48 flex items-center justify-center">
                    <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                  </div>
                ) : qrDataUrl ? (
                  <img src={qrDataUrl} alt="QR Code" className="w-48 h-48 rounded-lg border border-slate-200" />
                ) : null}
                <div className="text-center">
                  <p className="text-sm font-medium text-slate-700">Company Code</p>
                  <p className="font-mono text-lg font-bold text-indigo-700 mt-0.5">{qrCompany.company_code}</p>
                </div>
                <div className="flex gap-3 w-full">
                  <button
                    onClick={downloadQr}
                    disabled={!qrDataUrl}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <ArrowDownTrayIcon className="w-4 h-4" />
                    Download
                  </button>
                  <button
                    onClick={printQr}
                    disabled={!qrDataUrl}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Print
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
